import { useState } from 'react';
import { getSignedMediaUrl } from '@/lib/utils';
import type { ImagePlaceholderProps } from '@/features/artists/types/types';

/**
 * Media renderer component that displays images or videos using getSignedMediaUrl
 */
export const MediaRenderer: React.FC<{
  mediaId: string | null;
  alt?: string;
  className?: string;
  isVideo?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
}> = ({ 
  mediaId, 
  alt = '', 
  className = '', 
  isVideo = false,
  autoPlay = false,
  muted = true,
  loop = true,
  playsInline = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    setHasError(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 text-gray-500 w-full h-full ${className}`}>
        <span className="text-xs tracking-widest uppercase">Media unavailable</span>
      </div>
    );
  }

  if (isVideo) {
    return (
      <video
        className={`w-full h-full object-cover ${className}`}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        onError={handleError}
        onLoadedData={handleLoad}
        style={{ opacity: isLoaded ? 1 : 0 }}
      >
        <source src={getSignedMediaUrl(mediaId, 'video').url} type="video/mp4" />
        <source src={getSignedMediaUrl(mediaId, 'video').url} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <img
      src={getSignedMediaUrl(mediaId, 'image').url}
      alt={alt}
      className={`w-full h-full object-cover ${className}`}
      onError={handleError}
      onLoad={handleLoad}
      style={{ opacity: isLoaded ? 1 : 0 }}
    />
  );
};

/**
 * Image placeholder component for displaying media placeholders (fallback)
 */
export const ImagePlaceholder: React.FC<ImagePlaceholderProps> = ({ 
  label, 
  className = '' 
}) => (
  <div className={`flex items-center justify-center bg-gray-800 text-gray-500 w-full h-full ${className}`}>
    <span className="text-xs tracking-widest uppercase">{label}</span>
  </div>
);
